export type BgwClusterId =
  | 'hautschutz'
  | 'gefahrstoffe'
  | 'ergonomie'
  | 'mechanik'
  | 'notfall';

export interface BgwMeasureDef {
  id: string;
  clusterId: BgwClusterId;
  title: string;
  /** Optional: short legal / BGW hint shown in UI */
  legalHint?: string;
  /** Measures that support optional name fields (Cluster 5) */
  nameFieldKey?: 'firstAiderName' | 'fireSafetyOfficerName';
}

export interface BgwClusterDef {
  id: BgwClusterId;
  title: string;
  shortLabel: string;
  legalRef?: string;
  measureIds: string[];
}

export interface BgwMeasureState {
  id: string;
  compliant: boolean;
  note: string;
}

export const BGW_CLUSTERS: BgwClusterDef[] = [
  {
    id: 'hautschutz',
    title: 'Cluster 1: Hautschutz & Feuchtarbeit',
    shortLabel: 'Hautschutz',
    legalRef: 'BGW / ArbSchG',
    measureIds: ['c1-nitril', 'c1-hautschutzplan', 'c1-schmuck'],
  },
  {
    id: 'gefahrstoffe',
    title: 'Cluster 2: Gefahrstoffe & Raumluft (TRGS 530)',
    shortLabel: 'Gefahrstoffe',
    legalRef: 'TRGS 530',
    measureIds: ['c2-blondierung', 'c2-lueftung', 'c2-sdb'],
  },
  {
    id: 'ergonomie',
    title: 'Cluster 3: Ergonomie & Physische Belastungen',
    shortLabel: 'Ergonomie',
    legalRef: 'BGW',
    measureIds: ['c3-stuehle', 'c3-rollhocker', 'c3-werkzeuge', 'c3-becken'],
  },
  {
    id: 'mechanik',
    title: 'Cluster 4: Mechanische & Elektrische Gefahren',
    shortLabel: 'Mechanik / E-Geräte',
    legalRef: 'DGUV V3',
    measureIds: ['c4-koecher', 'c4-sharpsafe', 'c4-dguv-v3'],
  },
  {
    id: 'notfall',
    title: 'Cluster 5: Notfall- & Betriebsorganisation',
    shortLabel: 'Notfall',
    legalRef: 'DGUV Vorschrift 2',
    measureIds: [
      'c5-verbandskasten',
      'c5-ersthelfer',
      'c5-brandschutzhelfer',
      'c5-feuerloescher',
      'c5-fluchtwege',
    ],
  },
];

export const BGW_MEASURES: BgwMeasureDef[] = [
  {
    id: 'c1-nitril',
    clusterId: 'hautschutz',
    title:
      'Bereitstellung geeigneter Einweg-Nitrilhandschuhe (puderfrei, CE-Kennzeichnung).',
    legalHint: 'Hautschutz bei Feuchtarbeit',
  },
  {
    id: 'c1-hautschutzplan',
    clusterId: 'hautschutz',
    title: '3-stufiger Hautschutzplan vorhanden und im Salon ausgehängt.',
    legalHint: 'Hautschutzplan',
  },
  {
    id: 'c1-schmuck',
    clusterId: 'hautschutz',
    title: 'Schmuck- und Ringverbot während Feuchtarbeiten strikt umgesetzt.',
  },
  {
    id: 'c2-blondierung',
    clusterId: 'gefahrstoffe',
    title:
      'Verwendung staubarmer Blondierungen (Granulate/Pasten) und geschlossenes Anmischen.',
    legalHint: 'TRGS 530',
  },
  {
    id: 'c2-lueftung',
    clusterId: 'gefahrstoffe',
    title:
      'Ausreichende Frischluftzufuhr / Be- und Entlüftung in Labor und Bedienbereich.',
    legalHint: 'TRGS 530',
  },
  {
    id: 'c2-sdb',
    clusterId: 'gefahrstoffe',
    title:
      'Sicherheitsdatenblätter aller chemischen Produkte physisch oder digital zugänglich.',
  },
  {
    id: 'c3-stuehle',
    clusterId: 'ergonomie',
    title: 'Höhenverstellbare Arbeits- und Pumpstühle für Bedienplätze.',
  },
  {
    id: 'c3-rollhocker',
    clusterId: 'ergonomie',
    title: 'Vorhandensein von Rollhockern / Stehhilfen.',
  },
  {
    id: 'c3-werkzeuge',
    clusterId: 'ergonomie',
    title: 'Ergonomische, lärm- und vibrationsarme Werkzeuge / Föhne.',
  },
  {
    id: 'c3-becken',
    clusterId: 'ergonomie',
    title: 'Ergonomisch anpassbare Waschbecken.',
  },
  {
    id: 'c4-koecher',
    clusterId: 'mechanik',
    title:
      'Schutzköcher / Halterungen für Scheren und Klingen zur Schnittvermeidung.',
  },
  {
    id: 'c4-sharpsafe',
    clusterId: 'mechanik',
    title:
      'Durchstichsichere Abwurfboxen (Sharp-Safe) für gebrauchte Rasierklingen.',
  },
  {
    id: 'c4-dguv-v3',
    clusterId: 'mechanik',
    title:
      'DGUV V3 Prüfung aller ortsveränderlichen elektrischen Betriebsmittel nachgewiesen.',
    legalHint: 'DGUV V3',
  },
  {
    id: 'c5-verbandskasten',
    clusterId: 'notfall',
    title: 'Verbandskasten nach DIN 13157 vorhanden und geprüft.',
  },
  {
    id: 'c5-ersthelfer',
    clusterId: 'notfall',
    title: 'Ersthelfer im Team benannt.',
    legalHint: 'DGUV Vorschrift 2',
    nameFieldKey: 'firstAiderName',
  },
  {
    id: 'c5-brandschutzhelfer',
    clusterId: 'notfall',
    title: 'Brandschutzhelfer im Team benannt.',
    legalHint: 'DGUV Vorschrift 2',
    nameFieldKey: 'fireSafetyOfficerName',
  },
  {
    id: 'c5-feuerloescher',
    clusterId: 'notfall',
    title: 'Feuerlöscher zugänglich und 2-jährliche Prüfung nachgewiesen.',
  },
  {
    id: 'c5-fluchtwege',
    clusterId: 'notfall',
    title: 'Flucht- und Rettungswege frei und gekennzeichnet.',
  },
];

/** Create default measure states: all compliant = true, empty notes */
export function createDefaultBgwMeasureStates(): BgwMeasureState[] {
  return BGW_MEASURES.map((m) => ({
    id: m.id,
    compliant: true,
    note: '',
  }));
}

export function getMeasuresForCluster(clusterId: BgwClusterId): BgwMeasureDef[] {
  return BGW_MEASURES.filter((m) => m.clusterId === clusterId);
}

export function getMeasureDef(id: string): BgwMeasureDef | undefined {
  return BGW_MEASURES.find((m) => m.id === id);
}
